import { ACCELERATOR_STEP, COMBO_DECAY_MS, COMBO_HOLD_MS, COMBO_MAX_TIER, COMBO_MIN_CPS } from '../data/balance';
import { comboRampMult } from './specials';
import type { GameState } from './types';

/**
 * Click combo: hold ≥3 nest clicks/sec for 5 s to climb a tier (max 10).
 * Each tier: +0.1× click value, +0.5% farm egg value, and banks
 * accelerator time for the next offline stretch. Below 3 cps you lose a
 * tier per second.
 */
export function recordComboClick(s: GameState, now: number): void {
  s.combo.clicks.push(now);
  if (s.combo.clicks.length > 64) s.combo.clicks.splice(0, s.combo.clicks.length - 64);
}

export function clicksPerSecond(s: GameState, now: number): number {
  const clicks = s.combo.clicks;
  const hard = now - 2000;
  let trim = 0;
  while (trim < clicks.length && clicks[trim]! < hard) trim++;
  if (trim) clicks.splice(0, trim);
  let n = 0;
  for (const t of clicks) if (t >= now - 1000) n++;
  return n;
}

export function updateCombo(s: GameState, now: number): void {
  const c = s.combo;
  if (clicksPerSecond(s, now) >= COMBO_MIN_CPS) {
    c.lowSince = 0;
    if (!c.highSince) c.highSince = now;
    const hold = COMBO_HOLD_MS / comboRampMult(s);
    if (c.tier < COMBO_MAX_TIER && now - c.highSince >= hold) {
      c.tier++;
      c.highSince = now;
    }
    return;
  }
  c.highSince = 0;
  if (c.tier === 0) {
    if (!c.lowSince) c.lowSince = now;
    return;
  }
  if (!c.lowSince) c.lowSince = now;
  const elapsed = now - c.lowSince;
  if (elapsed >= COMBO_DECAY_MS) {
    c.tier = Math.max(0, c.tier - Math.floor(elapsed / COMBO_DECAY_MS));
    c.lowSince = now;
  }
}

/** Online accelerator banking: tier × 0.1 ms of future offline time per ms played. */
export function bankAccelerator(s: GameState, dtMs: number): void {
  if (s.combo.tier > 0) s.acceleratorBank += dtMs * s.combo.tier * ACCELERATOR_STEP;
}

export function resetCombo(s: GameState): void {
  s.combo = { tier: 0, clicks: [], highSince: 0, lowSince: 0 };
}
